import { useEffect, useState, useCallback } from "react";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { notificationsApi } from "@services/api/notifications";
import type {
  OrgMemberPreferences,
  NotificationPreferencesPayload,
  NotifPrefValue,
} from "@services/api/notifications";
import type { InAppEventType } from "@store/inAppNotificationsStore";

// Note: "mention" is intentionally NOT configurable — @mentions always notify
// the mentioned member regardless of preferences (handled in messagerie_controller),
// so exposing a toggle here would be misleading dead config.
const EVENT_TYPES: InAppEventType[] = [
  "payment_received",
  "booking_created",
  "devis_signed",
  "document_signed",
  "ordre_status_changed",
  "avis_received",
  "new_message",
  "devis_auto_cancelled",
];

const EVENT_SHORT_LABELS: Record<InAppEventType, string> = {
  payment_received: "Paiement",
  booking_created: "Réservation",
  document_signed: "Document",
  new_message: "Message",
  devis_signed: "Devis signé",
  ordre_status_changed: "Statut",
  avis_received: "Avis",
  devis_auto_cancelled: "Devis annulé",
  mention: "Mention",
};

const CYCLE_ORDER: NotifPrefValue[] = ["always", "assigned", "off"];
const PREF_DISPLAY: Record<NotifPrefValue, { label: string; bg: string; text: string }> = {
  always: { label: "Toujours", bg: "bg-black", text: "text-white" },
  assigned: { label: "Assigné", bg: "bg-blue-500", text: "text-white" },
  off: { label: "Off", bg: "bg-neutral-100", text: "text-shadow_50" },
};

export function NotificationsAdminTable() {
  const [members, setMembers] = useState<OrgMemberPreferences[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrgPrefs = useCallback(async () => {
    try {
      const data = await notificationsApi.getOrgPreferences();
      setMembers(data);
    } catch (err) {
      console.warn("Failed to fetch org preferences:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgPrefs();
  }, [fetchOrgPrefs]);

  const cycleEventType = async (
    member: OrgMemberPreferences,
    eventType: InAppEventType,
  ) => {
    const currentPrefs = member.notification_preferences || {};
    const currentEventTypes = currentPrefs.event_types || {};
    const raw: any = currentEventTypes[eventType];
    const currentValue: NotifPrefValue =
      raw === true ? "always" : raw === false ? "off" : raw ?? "always";
    const currentIdx = CYCLE_ORDER.indexOf(currentValue);
    const newValue = CYCLE_ORDER[(currentIdx + 1) % CYCLE_ORDER.length];

    const newEventTypes = { ...currentEventTypes, [eventType]: newValue };

    // Optimistic update
    setMembers((prev) =>
      prev.map((m) => {
        if (m.user_id !== member.user_id) return m;
        return {
          ...m,
          notification_preferences: {
            ...currentPrefs,
            event_types: newEventTypes,
          } as NotificationPreferencesPayload,
        };
      }),
    );

    try {
      await notificationsApi.updateUserPreferences(member.user_id, {
        event_types: newEventTypes,
        ordre_status_filters: currentPrefs.ordre_status_filters ?? null,
      });
    } catch (err) {
      console.warn("Failed to update user preferences:", err);
      setMembers((prev) =>
        prev.map((m) => {
          if (m.user_id !== member.user_id) return m;
          return {
            ...m,
            notification_preferences:
              currentPrefs as NotificationPreferencesPayload,
          };
        }),
      );
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-2 mt-6">
        <Text1>Préférences de l'équipe</Text1>
        <Text2>Chargement...</Text2>
      </div>
    );
  }

  if (members.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 mt-6 w-900">
      <div className="flex flex-col">
        <Text1>Préférences de l'équipe</Text1>
        <Text2>
          Cliquez pour alterner : Toujours / Si assigné / Désactivé
        </Text2>
      </div>

      <div className="overflow-x-auto rounded-xl border-1 shadow-sm">
        <table className="w-full text-h3">
          <thead>
            <tr className="bg-neutral-50">
              <th className="text-left p-3 font-medium text-shadow_50 sticky left-0 bg-neutral-50">
                Membre
              </th>
              {EVENT_TYPES.map((et) => (
                <th
                  key={et}
                  className="p-2 font-medium text-shadow_50 text-center whitespace-nowrap"
                >
                  {EVENT_SHORT_LABELS[et]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const eventTypes =
                member.notification_preferences?.event_types || {};
              return (
                <tr
                  key={member.user_id}
                  className="border-t border-neutral-100 hover:bg-neutral-50"
                >
                  <td className="p-3 sticky left-0 bg-white">
                    <div className="flex items-center gap-2">
                      {member.photo_url ? (
                        <img
                          src={member.photo_url}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-neutral-200" />
                      )}
                      <span className="font-medium text-black truncate max-w-150">
                        {member.display_name}
                      </span>
                    </div>
                  </td>
                  {EVENT_TYPES.map((et) => {
                    const raw: any = eventTypes[et];
                    const value: NotifPrefValue =
                      raw === true ? "always" : raw === false ? "off" : raw ?? "always";
                    const display = PREF_DISPLAY[value] || PREF_DISPLAY["always"];
                    return (
                      <td key={et} className="p-2 text-center">
                        <button
                          onClick={() => cycleEventType(member, et)}
                          className={`px-2 py-1 rounded-md text-h4 font-medium transition-colors min-w-55 ${display.bg} ${display.text}`}
                        >
                          {display.label}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
